import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import TipTapEditor from './TipTapEditor';
import EventCalendar from './EventCalendar';
import { 
  Plus, 
  Search, 
  Calendar, 
  Newspaper, 
  Pin, 
  Heart, 
  MessageCircle,
  Edit,
  Trash2,
  Clock,
  User,
  List,
  CalendarDays,
  Building2,
  X,
  Users,
  Send
} from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

// XSS-Schutz: HTML-Inhalte aus News/Event-Beschreibung werden vor dem Render
// mit DOMPurify bereinigt. Erlaubt bewusst TipTap-Standardtags (p, br, ul, ol,
// li, strong/em/u/s, a, h1-h6, blockquote, code, pre) plus Formatier-Attribute.
// Blockiert automatisch: <script>, on*-Handler, javascript:-URLs, data:-URIs
// (außer Bildern, s. u.). target="_blank" auf Links bleibt erlaubt.
const sanitizeHtml = (dirty) => {
  if (!dirty) return '';
  return DOMPurify.sanitize(String(dirty), {
    ALLOWED_TAGS: [
      'p', 'br', 'span', 'div',
      'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'sub', 'sup',
      'a',
      'ul', 'ol', 'li',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'blockquote', 'code', 'pre',
      'hr',
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'title', 'class'],
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    ADD_ATTR: ['target'],
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'onchange', 'onsubmit', 'style'],
  });
};

const Intranet = () => {
  const { user, theme } = useAuth();
  const [activeTab, setActiveTab] = useState('news');
  const [eventView, setEventView] = useState('list'); // 'list' or 'calendar'
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewsDialog, setShowNewsDialog] = useState(false);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedDateEvents, setSelectedDateEvents] = useState([]);
  const [showDayEventsDialog, setShowDayEventsDialog] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);

  // Comments
  const [expandedComments, setExpandedComments] = useState({}); // { [newsId]: true }
  const [commentDrafts, setCommentDrafts] = useState({}); // { [newsId]: 'draft text' }
  const [submittingComment, setSubmittingComment] = useState(null); // newsId currently submitting

  // Form states
  const [newsForm, setNewsForm] = useState({
    title: '',
    content: '',
    cover_image_url: '',
    tags: '',
    is_pinned: false
  });

  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    is_all_day: false,
    department_id: '',
    is_home_game: false,
    notiz: '',
    besonderheiten: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [newsRes, eventsRes, deptsRes] = await Promise.all([
        axios.get('/intranet/news'),
        axios.get('/intranet/events'),
        axios.get('/admin/departments').catch(() => ({ data: [] }))
      ]);
      setNews(newsRes.data);
      setEvents(eventsRes.data);
      setDepartments(deptsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim() || searchQuery.length < 2) {
      setSearchResults(null);
      return;
    }
    try {
      const response = await axios.get(`/intranet/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchResults(response.data);
    } catch (error) {
      toast.error('Suche fehlgeschlagen');
    }
  };

  // News Functions
  const handleCreateNews = async () => {
    try {
      const payload = {
        ...newsForm,
        tags: newsForm.tags.split(',').map(t => t.trim()).filter(t => t)
      };
      
      if (editingNews) {
        await axios.patch(`/intranet/news/${editingNews.id}`, payload);
        toast.success('News aktualisiert');
      } else {
        await axios.post('/intranet/news', payload);
        toast.success('News erstellt');
      }
      
      setShowNewsDialog(false);
      setEditingNews(null);
      setNewsForm({ title: '', content: '', cover_image_url: '', tags: '', is_pinned: false });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const handleDeleteNews = async (newsId) => {
    if (!confirm('News wirklich löschen?')) return;
    try {
      await axios.delete(`/intranet/news/${newsId}`);
      toast.success('News gelöscht');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleReaction = async (newsId, type) => {
    try {
      await axios.post(`/intranet/news/${newsId}/reactions`, { reaction_type: type });
      fetchData();
    } catch (error) {
      toast.error('Reaktion fehlgeschlagen');
    }
  };

  const toggleComments = (newsId) => {
    setExpandedComments(prev => ({ ...prev, [newsId]: !prev[newsId] }));
  };

  const handleAddComment = async (newsId) => {
    const text = (commentDrafts[newsId] || '').trim();
    if (!text) return;
    setSubmittingComment(newsId);
    try {
      await axios.post(`/intranet/news/${newsId}/comments`, { content: text });
      setCommentDrafts(prev => ({ ...prev, [newsId]: '' }));
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kommentar fehlgeschlagen');
    } finally {
      setSubmittingComment(null);
    }
  };

  const handleDeleteComment = async (newsId, commentId) => {
    if (!confirm('Kommentar löschen?')) return;
    try {
      await axios.delete(`/intranet/news/${newsId}/comments/${commentId}`);
      toast.success('Kommentar gelöscht');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Löschen fehlgeschlagen');
    }
  };

  const openEditNews = (newsItem) => {
    setEditingNews(newsItem);
    setNewsForm({
      title: newsItem.title,
      content: newsItem.content,
      cover_image_url: newsItem.cover_image_url || '',
      tags: (newsItem.tags || []).join(', '),
      is_pinned: newsItem.is_pinned || false
    });
    setShowNewsDialog(true);
  };

  // Event Functions
  const handleCreateEvent = async () => {
    try {
      const payload = {
        ...eventForm,
        start_date: new Date(eventForm.start_date).toISOString(),
        end_date: eventForm.end_date ? new Date(eventForm.end_date).toISOString() : null,
        department_id: eventForm.department_id || null
      };
      
      if (editingEvent) {
        await axios.patch(`/intranet/events/${editingEvent.id}`, payload);
        toast.success('Event aktualisiert');
      } else {
        await axios.post('/intranet/events', payload);
        toast.success('Event erstellt');
      }
      
      setShowEventDialog(false);
      setEditingEvent(null);
      setEventForm({ title: '', description: '', start_date: '', end_date: '', is_all_day: false, department_id: '', is_home_game: false, notiz: '', besonderheiten: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!confirm('Event wirklich löschen?')) return;
    try {
      await axios.delete(`/intranet/events/${eventId}`);
      toast.success('Event gelöscht');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const openEditEvent = (event) => {
    setEditingEvent(event);
    setEventForm({
      title: event.title,
      description: event.description || '',
      start_date: event.start_date ? event.start_date.slice(0, 16) : '',
      end_date: event.end_date ? event.end_date.slice(0, 16) : '',
      is_all_day: event.is_all_day || false,
      department_id: event.department_id || '',
      is_home_game: event.is_home_game || false,
      notiz: event.notiz || '',
      besonderheiten: event.besonderheiten || ''
    });
    setShowEventDialog(true);
  };

  const handleCalendarDateSelect = (date, dayEvents) => {
    setSelectedDay(date);
    setSelectedDateEvents(dayEvents);
    if (dayEvents.length > 0) {
      setShowDayEventsDialog(true);
    }
  };

  const handleCalendarEventSelect = (event) => {
    openEditEvent(event);
  };

  const canEditNews = (newsItem) => {
    return newsItem.author_id === user.id || user.role === 'super_admin' || user.role === 'admin';
  };

  const canEditEvent = (event) => {
    return event.author_id === user.id || user.role === 'super_admin' || user.role === 'admin';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDepartmentById = (id) => {
    return departments.find(d => d.id === id);
  };

  // Filter events by department
  const filteredEvents = selectedDepartment === 'all' 
    ? events 
    : events.filter(e => e.department_id === selectedDepartment);

  const cardBg = theme === 'dark' ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#e30613] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="intranet-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Willkommen, {user.name || user.email.split('@')[0]}!</h1>
          <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Aktuelle News und Events der Kölner Haie
          </p>
        </div>
        
        {/* Search */}
        <div className="flex gap-2 w-full sm:w-auto">
          <Input
            placeholder="Suchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            className="w-full sm:w-64"
            data-testid="search-input"
          />
          <Button onClick={handleSearch} variant="outline" data-testid="search-button">
            <Search className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Search Results */}
      {searchResults && (
        <Card className={cardBg}>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center justify-between">
              Suchergebnisse für "{searchQuery}"
              <Button variant="ghost" size="sm" onClick={() => setSearchResults(null)}>
                <X className="w-4 h-4" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {searchResults.news.length === 0 && searchResults.events.length === 0 ? (
              <p className="text-gray-500">Keine Ergebnisse gefunden</p>
            ) : (
              <div className="space-y-4">
                {searchResults.news.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2">News ({searchResults.news.length})</h4>
                    {searchResults.news.map(item => (
                      <div key={item.id} className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer">
                        <p className="font-medium">{item.title}</p>
                        <p className="text-sm text-gray-500">{formatDate(item.created_at)}</p>
                      </div>
                    ))}
                  </div>
                )}
                {searchResults.events.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2">Events ({searchResults.events.length})</h4>
                    {searchResults.events.map(item => (
                      <div key={item.id} className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer">
                        <p className="font-medium">{item.title}</p>
                        <p className="text-sm text-gray-500">{formatDate(item.start_date)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <TabsList className={theme === 'dark' ? 'bg-gray-900' : 'bg-gray-100'}>
            <TabsTrigger value="news" className="flex items-center gap-2" data-testid="tab-news">
              <Newspaper className="w-4 h-4" />
              News
            </TabsTrigger>
            <TabsTrigger value="events" className="flex items-center gap-2" data-testid="tab-events">
              <Calendar className="w-4 h-4" />
              Events
            </TabsTrigger>
          </TabsList>

          {/* Create Buttons & View Toggle */}
          <div className="flex gap-2 items-center">
            {activeTab === 'events' && (
              <>
                {/* Department Filter */}
                <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
                  <SelectTrigger className="w-40">
                    <Building2 className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Abteilung" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Abteilungen</SelectItem>
                    {departments.map(dept => (
                      <SelectItem key={dept.id} value={dept.id}>
                        <span className="flex items-center gap-2">
                          <span 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: dept.color }}
                          />
                          {dept.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* View Toggle */}
                <div className="flex border rounded-lg overflow-hidden">
                  <Button
                    variant={eventView === 'list' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setEventView('list')}
                    className={eventView === 'list' ? 'bg-[#e30613] hover:bg-[#c00510]' : ''}
                  >
                    <List className="w-4 h-4" />
                  </Button>
                  <Button
                    variant={eventView === 'calendar' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setEventView('calendar')}
                    className={eventView === 'calendar' ? 'bg-[#e30613] hover:bg-[#c00510]' : ''}
                  >
                    <CalendarDays className="w-4 h-4" />
                  </Button>
                </div>
              </>
            )}

            {activeTab === 'news' && (
              <Dialog open={showNewsDialog} onOpenChange={(open) => {
                setShowNewsDialog(open);
                if (!open) {
                  setEditingNews(null);
                  setNewsForm({ title: '', content: '', cover_image_url: '', tags: '', is_pinned: false });
                }
              }}>
                <DialogTrigger asChild>
                  <Button className="bg-[#e30613] hover:bg-[#c00510]" data-testid="create-news-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    News erstellen
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{editingNews ? 'News bearbeiten' : 'Neue News erstellen'}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Titel *</Label>
                      <Input
                        value={newsForm.title}
                        onChange={(e) => setNewsForm({...newsForm, title: e.target.value})}
                        placeholder="Titel der News"
                        data-testid="news-title-input"
                      />
                    </div>
                    <div>
                      <Label>Inhalt *</Label>
                      <TipTapEditor
                        content={newsForm.content}
                        onChange={(html) => setNewsForm({...newsForm, content: html})}
                        placeholder="Schreiben Sie Ihre News hier..."
                      />
                    </div>
                    <div>
                      <Label>Cover-Bild URL (optional)</Label>
                      <Input
                        value={newsForm.cover_image_url}
                        onChange={(e) => setNewsForm({...newsForm, cover_image_url: e.target.value})}
                        placeholder="https://..."
                      />
                    </div>
                    <div>
                      <Label>Tags (kommagetrennt)</Label>
                      <Input
                        value={newsForm.tags}
                        onChange={(e) => setNewsForm({...newsForm, tags: e.target.value})}
                        placeholder="Wichtig, Team, Update"
                      />
                    </div>
                    {(user.role === 'super_admin' || user.role === 'admin') && (
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="is_pinned"
                          checked={newsForm.is_pinned}
                          onChange={(e) => setNewsForm({...newsForm, is_pinned: e.target.checked})}
                        />
                        <Label htmlFor="is_pinned">Anpinnen (oben anzeigen)</Label>
                      </div>
                    )}
                    <Button 
                      onClick={handleCreateNews} 
                      className="w-full bg-[#e30613] hover:bg-[#c00510]"
                      disabled={!newsForm.title || !newsForm.content}
                      data-testid="save-news-btn"
                    >
                      {editingNews ? 'Speichern' : 'Erstellen'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
            
            {activeTab === 'events' && (
              <Dialog open={showEventDialog} onOpenChange={(open) => {
                setShowEventDialog(open);
                if (!open) {
                  setEditingEvent(null);
                  setEventForm({ title: '', description: '', start_date: '', end_date: '', is_all_day: false, department_id: '', is_home_game: false, notiz: '', besonderheiten: '' });
                }
              }}>
                <DialogTrigger asChild>
                  <Button className="bg-[#e30613] hover:bg-[#c00510]" data-testid="create-event-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Event erstellen
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{editingEvent ? 'Event bearbeiten' : 'Neues Event erstellen'}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Titel *</Label>
                      <Input
                        value={eventForm.title}
                        onChange={(e) => setEventForm({...eventForm, title: e.target.value})}
                        placeholder="Event-Titel"
                        data-testid="event-title-input"
                      />
                    </div>
                    <div>
                      <Label>Beschreibung</Label>
                      <TipTapEditor
                        content={eventForm.description}
                        onChange={(html) => setEventForm({...eventForm, description: html})}
                        placeholder="Beschreibung des Events..."
                      />
                    </div>
                    <div>
                      <Label>Abteilung</Label>
                      <Select 
                        value={eventForm.department_id || "none"} 
                        onValueChange={(value) => setEventForm({...eventForm, department_id: value === "none" ? "" : value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Abteilung auswählen (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Keine Abteilung</SelectItem>
                          {departments.map(dept => (
                            <SelectItem key={dept.id} value={dept.id}>
                              <span className="flex items-center gap-2">
                                <span 
                                  className="w-3 h-3 rounded-full" 
                                  style={{ backgroundColor: dept.color }}
                                />
                                {dept.name}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Startdatum *</Label>
                        <Input
                          type="datetime-local"
                          value={eventForm.start_date}
                          onChange={(e) => setEventForm({...eventForm, start_date: e.target.value})}
                          data-testid="event-start-input"
                        />
                      </div>
                      <div>
                        <Label>Enddatum</Label>
                        <Input
                          type="datetime-local"
                          value={eventForm.end_date}
                          onChange={(e) => setEventForm({...eventForm, end_date: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="is_all_day"
                        checked={eventForm.is_all_day}
                        onChange={(e) => setEventForm({...eventForm, is_all_day: e.target.checked})}
                      />
                      <Label htmlFor="is_all_day">Ganztägig</Label>
                    </div>
                    
                    {/* Tischplan Option - only for admins */}
                    {(user.role === 'super_admin' || user.role === 'admin') && (
                      <div className={`p-4 rounded-lg border ${eventForm.is_home_game ? 'border-[#e30613] bg-[#e30613]/5' : 'border-gray-200 dark:border-gray-700'}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <input
                            type="checkbox"
                            id="is_home_game"
                            checked={eventForm.is_home_game}
                            onChange={(e) => setEventForm({...eventForm, is_home_game: e.target.checked})}
                          />
                          <Label htmlFor="is_home_game" className="flex items-center gap-2 font-medium">
                            <Users className="w-4 h-4" />
                            Heimspieltag
                          </Label>
                        </div>
                        <p className="text-xs text-gray-500 ml-6">
                          Aktiviert Tischplanung und Mitarbeiter-Tickets für dieses Event
                        </p>
                        
                        {eventForm.is_home_game && (
                          <div className="mt-4 space-y-3 ml-6">
                            <div>
                              <Label className="text-sm">Interne Notiz</Label>
                              <Input
                                value={eventForm.notiz}
                                onChange={(e) => setEventForm({...eventForm, notiz: e.target.value})}
                                placeholder="Interne Notizen zum Event"
                                className="mt-1"
                              />
                            </div>
                            <div>
                              <Label className="text-sm">Besonderheiten</Label>
                              <Input
                                value={eventForm.besonderheiten}
                                onChange={(e) => setEventForm({...eventForm, besonderheiten: e.target.value})}
                                placeholder="Besondere Anforderungen"
                                className="mt-1"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    <Button 
                      onClick={handleCreateEvent} 
                      className="w-full bg-[#e30613] hover:bg-[#c00510]"
                      disabled={!eventForm.title || !eventForm.start_date}
                      data-testid="save-event-btn"
                    >
                      {editingEvent ? 'Speichern' : 'Erstellen'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* News Tab */}
        <TabsContent value="news" className="mt-6">
          {news.length === 0 ? (
            <Card className={cardBg}>
              <CardContent className="py-12 text-center">
                <Newspaper className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p className="text-gray-500">Noch keine News vorhanden</p>
                <p className="text-sm text-gray-400">Erstellen Sie die erste News!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {news.map(item => (
                <Card key={item.id} className={`${cardBg} overflow-hidden`} data-testid={`news-card-${item.id}`}>
                  {item.cover_image_url && (
                    <div className="h-48 overflow-hidden">
                      <img 
                        src={item.cover_image_url} 
                        alt={item.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          {item.is_pinned && (
                            <Badge variant="outline" className="text-[#e30613] border-[#e30613]">
                              <Pin className="w-3 h-3 mr-1" />
                              Angepinnt
                            </Badge>
                          )}
                          {item.tags?.map(tag => (
                            <Badge key={tag} variant="secondary" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                        <CardTitle className="text-xl">{item.title}</CardTitle>
                        <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                          <User className="w-4 h-4" />
                          <span>{item.author_name}</span>
                          <span>·</span>
                          <Clock className="w-4 h-4" />
                          <span>{formatDate(item.created_at)}</span>
                        </div>
                      </div>
                      {canEditNews(item) && (
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => openEditNews(item)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleDeleteNews(item.id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div 
                      className="prose prose-sm max-w-none dark:prose-invert"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.content) }}
                    />
                    
                    {/* Reactions & Comments */}
                    <div className="flex items-center gap-4 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleReaction(item.id, 'like')}
                        className={item.reactions?.some(r => r.user_id === user.id && r.reaction_type === 'like') ? 'text-[#e30613]' : ''}
                      >
                        <Heart className="w-4 h-4 mr-1" />
                        {item.reactions?.filter(r => r.reaction_type === 'like').length || 0}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleComments(item.id)}
                        data-testid={`toggle-comments-${item.id}`}
                        className={expandedComments[item.id] ? 'text-[#e30613]' : ''}
                      >
                        <MessageCircle className="w-4 h-4 mr-1" />
                        {item.comments?.length || 0} Kommentare
                      </Button>
                    </div>

                    {/* Comments Section */}
                    {expandedComments[item.id] && (
                      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 space-y-3" data-testid={`comments-section-${item.id}`}>
                        {(item.comments || []).length === 0 ? (
                          <p className="text-sm text-gray-400 text-center py-2">Noch keine Kommentare. Sei der Erste!</p>
                        ) : (
                          <div className="space-y-2">
                            {item.comments.map(c => {
                              const isAuthor = c.author_id === user.id;
                              const canDelete = isAuthor || user.role === 'admin';
                              return (
                                <div
                                  key={c.id}
                                  className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 group"
                                  data-testid={`comment-${c.id}`}
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className="text-xs font-semibold">{c.author_name}</span>
                                        <span className="text-[10px] text-gray-400">
                                          {c.created_at && format(new Date(c.created_at), 'dd.MM.yyyy HH:mm', { locale: de })}
                                        </span>
                                      </div>
                                      <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
                                        {c.content}
                                      </p>
                                    </div>
                                    {canDelete && (
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                        onClick={() => handleDeleteComment(item.id, c.id)}
                                        data-testid={`delete-comment-${c.id}`}
                                        aria-label="Kommentar löschen"
                                      >
                                        <Trash2 className="w-3 h-3 text-red-500" />
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* New comment input */}
                        <div className="flex gap-2 items-start">
                          <Textarea
                            value={commentDrafts[item.id] || ''}
                            onChange={(e) => setCommentDrafts(prev => ({ ...prev, [item.id]: e.target.value }))}
                            placeholder="Kommentar schreiben…"
                            className="text-sm min-h-[60px] resize-y"
                            data-testid={`comment-input-${item.id}`}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                                e.preventDefault();
                                handleAddComment(item.id);
                              }
                            }}
                          />
                          <Button
                            size="sm"
                            onClick={() => handleAddComment(item.id)}
                            disabled={!commentDrafts[item.id]?.trim() || submittingComment === item.id}
                            data-testid={`submit-comment-${item.id}`}
                            className="bg-[#e30613] hover:bg-[#c00510]"
                          >
                            <Send className="w-4 h-4" />
                          </Button>
                        </div>
                        <p className="text-[10px] text-gray-400 -mt-1">Tipp: Cmd/Ctrl + Enter zum Senden</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Events Tab */}
        <TabsContent value="events" className="mt-6">
          {eventView === 'calendar' ? (
            <EventCalendar 
              events={filteredEvents}
              onSelectDate={handleCalendarDateSelect}
              onSelectEvent={handleCalendarEventSelect}
            />
          ) : (
            <>
              {filteredEvents.length === 0 ? (
                <Card className={cardBg}>
                  <CardContent className="py-12 text-center">
                    <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                    <p className="text-gray-500">Keine Events gefunden</p>
                    <p className="text-sm text-gray-400">
                      {selectedDepartment !== 'all' 
                        ? 'Versuchen Sie einen anderen Filter'
                        : 'Erstellen Sie das erste Event!'}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {filteredEvents.map(event => {
                    const dept = getDepartmentById(event.department_id);
                    return (
                      <Card key={event.id} className={cardBg} data-testid={`event-card-${event.id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex gap-4">
                              <div className="text-center min-w-[60px]">
                                <div className="text-3xl font-bold text-[#e30613]">
                                  {new Date(event.start_date).getDate()}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {new Date(event.start_date).toLocaleDateString('de-DE', { month: 'short' })}
                                </div>
                              </div>
                              <div>
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                  <h3 className="font-semibold text-lg">{event.title}</h3>
                                  {event.is_home_game && (
                                    <Badge className="bg-[#e30613] text-white">
                                      <Users className="w-3 h-3 mr-1" />
                                      Heimspiel
                                    </Badge>
                                  )}
                                  {dept && (
                                    <Badge 
                                      variant="outline" 
                                      style={{ borderColor: dept.color, color: dept.color }}
                                    >
                                      {dept.name}
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-gray-500">
                                  {event.is_all_day ? 'Ganztägig' : formatDate(event.start_date)}
                                  {event.end_date && ` - ${formatDate(event.end_date)}`}
                                </p>
                                {event.description && (
                                  <div 
                                    className="text-sm mt-2 text-gray-600 dark:text-gray-400 prose prose-sm max-w-none dark:prose-invert"
                                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(event.description) }}
                                  />
                                )}
                                <div className="flex items-center gap-4 mt-2">
                                  <p className="text-xs text-gray-400">
                                    Erstellt von {event.author_name}
                                  </p>
                                  {event.is_home_game && (
                                    <a 
                                      href={`/tischplan/${event.id}`}
                                      className="text-xs text-[#e30613] hover:underline flex items-center gap-1"
                                    >
                                      <Users className="w-3 h-3" />
                                      Zum Tischplan →
                                    </a>
                                  )}
                                </div>
                              </div>
                            </div>
                            {canEditEvent(event) && (
                              <div className="flex gap-1">
                                {event.is_home_game && (
                                  <Button 
                                    variant="ghost" 
                                    size="icon"
                                    onClick={() => window.location.href = `/tischplan/${event.id}`}
                                    title="Tischplan öffnen"
                                  >
                                    <Users className="w-4 h-4" />
                                  </Button>
                                )}
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  onClick={() => openEditEvent(event)}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  onClick={() => handleDeleteEvent(event.id)}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Day Events Dialog (from calendar click) */}
      <Dialog open={showDayEventsDialog} onOpenChange={setShowDayEventsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Events am {selectedDay?.toLocaleDateString('de-DE', { 
                weekday: 'long', 
                day: 'numeric', 
                month: 'long', 
                year: 'numeric' 
              })}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {selectedDateEvents.map(event => {
              const dept = getDepartmentById(event.department_id);
              return (
                <div 
                  key={event.id} 
                  className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                  onClick={() => {
                    setShowDayEventsDialog(false);
                    openEditEvent(event);
                  }}
                >
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium">{event.title}</h4>
                    {dept && (
                      <Badge 
                        variant="outline" 
                        style={{ borderColor: dept.color, color: dept.color }}
                        className="text-xs"
                      >
                        {dept.name}
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">
                    {event.is_all_day ? 'Ganztägig' : new Date(event.start_date).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Intranet;
